import React, { useEffect, useRef, useState } from 'react';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { languageLabels, languages, useLocalization } from '../../localization';

const navLinkStyle = {
  fontFamily: 'SuisseCond',
  fontSize: '22px',
  fontWeight: 'bold',
  color: '#fff',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
  textShadow: '0 0 4px rgba(0, 0, 0, 0.8)',
} as const;

const languageShortLabels = {
  ca: 'CAT',
  es: 'ES',
  en: 'EN',
} as const;

const Header: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { copy, language, setLanguage, projectPagePath } = useLocalization();

  useEffect(() => {
    setExpanded(false);
    setLanguageMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeLanguageMenu = (event: MouseEvent) => {
      if (!languageMenuRef.current?.contains(event.target as Node)) {
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeLanguageMenu);
    return () => document.removeEventListener('mousedown', closeLanguageMenu);
  }, []);

  return (
    <div
      ref={languageMenuRef}
      style={{
        position: 'sticky',
        top: 0,
        padding: '10px',
        zIndex: 1020,
      }}
    >
      <style>
        {`
          .nav-link-custom {
            position: relative;
            padding: 8px 16px !important;
            margin: 0 4px;
            border-radius: 8px;
          }

          .nav-link-custom:hover {
            background-color: rgba(255, 255, 255, 0.2) !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }

          .mobile-menu-shell {
            display: none;
          }

          .header-language {
            align-items: center;
            border: 1px solid transparent;
            border-radius: 999px;
            box-sizing: border-box;
            display: flex;
            margin-left: 1rem;
            position: relative;
            transition: background-color 0.25s ease, border-color 0.25s ease, width 0.25s ease;
            width: 42px;
          }

          .header-language.is-open {
            background-color: rgba(255, 255, 255, 0.15);
            border: 1px solid rgba(255, 255, 255, 0.35);
            overflow: hidden;
            width: 170px;
          }

          .language-menu-trigger {
            align-items: center;
            background-color: rgba(255, 255, 255, 0.12);
            border: 1px solid rgba(255, 255, 255, 0.35);
            border-radius: 999px;
            color: #fff;
            cursor: pointer;
            display: inline-flex;
            flex: 0 0 40px;
            height: 40px;
            justify-content: center;
            padding: 0;
            transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
            width: 40px;
          }

          .language-menu-trigger:hover,
          .language-menu-trigger[aria-expanded='false'] {
            background-color: rgba(255, 255, 255, 0.2);
            border-color: rgba(255, 255, 255, 0.6);
            transform: translateY(-1px);
          }

          .header-language.is-open .language-menu-trigger {
            background: transparent;
            border-color: transparent;
            border-radius: 0;
            transform: none;
          }

          .language-menu-trigger:focus-visible,
          .language-menu-option:focus-visible {
            box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.25);
            outline: none;
          }

          .language-menu-panel {
            align-items: center;
            border: none;
            display: flex;
            gap: 0;
            opacity: 0;
            overflow: hidden;
            padding: 0;
            pointer-events: none;
            transform: translateX(-0.5rem);
            transition: opacity 0.18s ease, transform 0.25s ease, width 0.25s ease;
            width: 0;
          }

          .header-language.is-open .language-menu-panel {
            gap: 0.25rem;
            opacity: 1;
            padding: 0;
            pointer-events: auto;
            transform: translateX(0);
            width: 128px;
          }

          .language-menu-option {
            align-items: center;
            background: transparent;
            border: 1px solid transparent;
            border-radius: 999px;
            cursor: pointer;
            display: inline-flex;
            color: #fff;
            font-family: 'SuisseCond';
            font-size: 1rem;
            font-weight: bold;
            height: 40px;
            justify-content: center;
            padding: 0;
            transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
            width: 40px;
          }

          .language-menu-option:hover,
          .language-menu-option[aria-current='true'] {
            background: rgba(255, 255, 255, 0.28);
            border-color: rgba(255, 255, 255, 0.65);
            transform: none;
          }

          .language-menu-option[aria-current='true'] {
            border-radius: 999px;
          }

          @media (max-width: 991.98px) {
            .desktop-nav {
              display: none !important;
            }

            .mobile-menu-toggle {
              align-items: center;
              background: rgba(255, 255, 255, 0.08);
              border-color: rgba(255, 255, 255, 0.28) !important;
              border-radius: 999px;
              display: inline-flex;
              height: 44px;
              justify-content: center;
              padding: 0;
              transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
              width: 44px;
            }

            .mobile-menu-toggle:hover {
              background: rgba(255, 255, 255, 0.14);
              border-color: rgba(255, 255, 255, 0.4) !important;
              transform: translateY(-1px);
            }

            .mobile-menu-toggle:focus {
              box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.18) !important;
            }

            .mobile-menu-toggle .navbar-toggler-icon {
              background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%28255, 255, 255, 0.92%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2.2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
              height: 20px;
              width: 20px;
            }

            .mobile-menu-shell {
              display: block;
              left: 10px;
              position: absolute;
              right: 10px;
              top: calc(100% + 8px);
              z-index: 1021;
            }

            .mobile-menu-panel {
              background-color: rgba(255, 255, 255, 0.15);
              backdrop-filter: blur(10px);
              -webkit-backdrop-filter: blur(10px);
              border: none;
              border-radius: 20px;
              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
              padding: 0.75rem;
            }

            .mobile-menu-nav {
              display: flex;
              flex-direction: column;
              gap: 0.25rem;
            }

            .mobile-menu-nav .nav-link-custom {
              margin: 0;
            }

            .desktop-language {
              display: none;
            }

            .mobile-language {
              display: flex;
            }

            .header-language {
              margin-left: auto;
              margin-right: 0.75rem;
            }

            .mobile-menu-language {
              border-top: 1px solid rgba(255, 255, 255, 0.28);
              display: flex;
              margin-top: 0.6rem;
              padding-top: 0.75rem;
            }

            .mobile-menu-language .mobile-language {
              display: flex;
              margin: 0;
            }
          }

          .mobile-language {
            display: none;
          }
        `}
      </style>
      <Navbar
        expand="lg"
        className="py-3"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: 'none',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          overflow: 'visible',
          transition: 'all 0.3s ease',
          width: 'min(100%, 1400px)',
          margin: '0 auto',
        }}
      >
        <Container>
          <Navbar.Brand as={Link} to="/">
            <img
              src="/img/pumalogo.svg"
              alt={copy.header.logoAlt}
              style={{
                paddingTop: '5px',
                filter: 'brightness(0) invert(1) drop-shadow(0 0 4px rgba(0, 0, 0, 0.8))',
              }}
              height="48"
              className="d-inline-block align-top me-2"
            />
          </Navbar.Brand>

          <button
            type="button"
            aria-controls="mobile-nav-menu"
            aria-expanded={expanded}
            aria-label={copy.header.toggleNavigation}
            className="navbar-toggler mobile-menu-toggle d-lg-none"
            onClick={() => setExpanded((current) => !current)}
          >
            <span className="navbar-toggler-icon" />
          </button>

          <Nav className="ms-auto desktop-nav">
            <Nav.Link as={Link} to="/" className="nav-link-custom" style={navLinkStyle}>
              {copy.header.home}
            </Nav.Link>
            <Nav.Link as={Link} to="/info" className="nav-link-custom" style={navLinkStyle}>
              {copy.header.info}
            </Nav.Link>
            <Nav.Link as={Link} to={projectPagePath} className="nav-link-custom" style={navLinkStyle}>
              {copy.header.project}
            </Nav.Link>
          </Nav>
          <section className={`header-language desktop-language${languageMenuOpen ? ' is-open' : ''}`}>
            <button
              type="button"
              className="language-menu-trigger"
              aria-label={copy.header.language}
              aria-controls="language-menu"
              aria-expanded={languageMenuOpen}
              onClick={() => setLanguageMenuOpen((isOpen) => !isOpen)}
            >
              <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
                <path d="M3.8 12h16.4M12 3.5c2.25 2.35 3.4 5.18 3.4 8.5S14.25 18.15 12 20.5C9.75 18.15 8.6 15.32 8.6 12S9.75 5.85 12 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <div
              className="language-menu-panel"
              id="language-menu"
              role="menu"
              aria-hidden={!languageMenuOpen}
            >
              {languages.map((option) => (
                <button
                  key={option}
                  type="button"
                  className="language-menu-option"
                  role="menuitemradio"
                  aria-checked={language === option}
                  aria-current={language === option ? 'true' : undefined}
                  aria-label={languageLabels[option]}
                  title={languageLabels[option]}
                  tabIndex={languageMenuOpen ? 0 : -1}
                  onClick={() => {
                    setLanguage(option);
                    setLanguageMenuOpen(false);
                  }}
                >
                  <span aria-hidden="true">{languageShortLabels[option]}</span>
                </button>
              ))}
            </div>
          </section>
        </Container>
      </Navbar>

      {expanded ? (
        <div className="mobile-menu-shell d-lg-none" id="mobile-nav-menu">
          <div className="mobile-menu-panel">
            <Nav className="mobile-menu-nav">
              <Nav.Link as={Link} to="/" className="nav-link-custom" style={navLinkStyle}>
                {copy.header.home}
              </Nav.Link>
              <Nav.Link as={Link} to="/info" className="nav-link-custom" style={navLinkStyle}>
                {copy.header.info}
              </Nav.Link>
              <Nav.Link as={Link} to={projectPagePath} className="nav-link-custom" style={navLinkStyle}>
                {copy.header.project}
              </Nav.Link>
            </Nav>
            <div className="mobile-menu-language">
              <section className="header-language mobile-language is-open" aria-label={copy.header.language}>
                <span className="language-menu-trigger" aria-hidden="true">
                  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M3.8 12h16.4M12 3.5c2.25 2.35 3.4 5.18 3.4 8.5S14.25 18.15 12 20.5C9.75 18.15 8.6 15.32 8.6 12S9.75 5.85 12 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <div
                  className="language-menu-panel"
                  id="mobile-language-menu"
                  role="menu"
                  aria-hidden="false"
                >
                  {languages.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className="language-menu-option"
                      role="menuitemradio"
                      aria-checked={language === option}
                      aria-current={language === option ? 'true' : undefined}
                      aria-label={languageLabels[option]}
                      title={languageLabels[option]}
                      tabIndex={0}
                      onClick={() => {
                        setLanguage(option);
                        setLanguageMenuOpen(false);
                      }}
                    >
                      <span aria-hidden="true">{languageShortLabels[option]}</span>
                    </button>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Header;
